import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchClawHealth,
  fetchGovernanceSummary,
  fetchAuditPage,
  fetchAuditIntegrity,
  fetchAuditAnchors,
  fetchDiscoveryPreview,
  fetchHealth,
  fetchQuantumStatus,
  fetchPipelineStages,
  checkApiOnline,
  postClawAuthorize,
  postFleetHalt,
  postFleetResume,
  fetchThreatGraphStats,
} from '@/lib/api';

// Fleet data hook - auto-refreshes every 5 seconds
export function useFleetData() {
  const [data, setData] = useState<{ claw: any; governance: any; pipeline: any } | null>(null);
  const [loading, setLoading] = useState(true);
  // Pipeline stages are static (the 10-stage chain does not change at runtime), so fetch them
  // once and cache. Polling them every 5s needlessly hammers /api/pipeline/stages and trips its
  // rate limiter (429); only the dynamic endpoints (claw health, governance summary) are polled.
  const pipelineRef = useRef<any>(null);

  const load = useCallback(async () => {
    if (!pipelineRef.current) {
      pipelineRef.current = await fetchPipelineStages();
    }
    const [claw, governance] = await Promise.all([
      fetchClawHealth(),
      fetchGovernanceSummary(),
    ]);
    setData({ claw, governance, pipeline: pipelineRef.current });
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [load]);

  return { data, loading, refresh: load };
}

// Audit data hook with pagination
export function useAuditData() {
  const [entries, setEntries] = useState<any[]>([]);
  const [integrity, setIntegrity] = useState<any>(null);
  const [anchors, setAnchors] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    const [auditData, integrityData, anchorData] = await Promise.all([
      fetchAuditPage(page),
      fetchAuditIntegrity(),
      fetchAuditAnchors(),
    ]);
    if (auditData) {
      // The actual /api/audit/export response shape is { success, source, data: [...], pagination: {page,limit,total,totalPages} }.
      // Older shapes used { entries: [...] } or { blocks: [...] } -- check all three so we don't silently render empty when the table actually has 500+ rows.
      setEntries(auditData.data || auditData.entries || auditData.blocks || []);
      const pag = auditData.pagination || {};
      setTotalPages(pag.totalPages || auditData.totalPages || Math.ceil((pag.total || auditData.total || 0) / 20) || 1);
    }
    setIntegrity(integrityData);
    setAnchors(anchorData);
    setLoading(false);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return { entries, integrity, anchors, page, totalPages, setPage, loading, refresh: load };
}

// Steer data hook - shares data with fleet
export function useSteerData() {
  const [data, setData] = useState<{ claw: any; governance: any } | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [claw, governance] = await Promise.all([
      fetchClawHealth(),
      fetchGovernanceSummary(),
    ]);
    setData({ claw, governance });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return { data, loading, refresh: load };
}

// Discovery data hook
export function useDiscoveryData() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDiscoveryPreview().then(d => { setData(d); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

// Security status hook
export function useSecurityStatusData() {
  const [data, setData] = useState<{ health: any; quantum: any; governance: any } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchHealth(), fetchQuantumStatus(), fetchGovernanceSummary()]).then(
      ([health, quantum, governance]) => {
        setData({ health, quantum, governance });
      }
    ).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

// Threat-intelligence graph hook. Auto-refreshes every 10s so new signatures surface without
// a manual reload. The API decides the response SHAPE from the caller's identity (operator
// aggregate + moat metric vs tenant own-posture); the view keys on data.scope to render the
// right audience. The audience boundary is enforced server-side, not by this hook.
export function useThreatGraphData() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const d = await fetchThreatGraphStats();
    setData(d);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  return { data, loading, refresh: load };
}

// Online status hook
export function useOnlineStatus() {
  const [online, setOnline] = useState(true);

  useEffect(() => {
    const check = async () => setOnline(await checkApiOnline());
    check();
    const interval = setInterval(check, 10000);
    return () => clearInterval(interval);
  }, []);

  return online;
}

// Demo mode hook -- exercises the actual governance engine, not a tab tour.
// Each step CALLS a real engine endpoint and surfaces the engine response so the user
// sees the system blocking actions + halting agents + writing audit entries in real time.
// Replaces the prior tab-scrolling implementation that was marketing copy with no engine action.
export function useDemoMode() {
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);
  const [evidence, setEvidence] = useState<DemoEvidence[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  // Each step describes (a) which view to land on, (b) the user-facing message, and
  // (c) an `action` function that hits the real engine and returns demonstrable evidence
  // (audit id, halt count, deny reason, etc) the user can verify.
  const STEPS: DemoStep[] = [
    {
      view: 'fleet',
      message: 'Submitting a forbidden action -- Analyst tier requesting db:read...',
      action: async () => {
        const r = await postClawAuthorize({ agentId: 'demo-agent', action: 'db:read', tier: 'Analyst' });
        return { kind: 'authorize-denied', authorized: r?.authorized, reason: r?.denyReason?.message, auditId: r?.auditId, tier: r?.tier };
      },
    },
    {
      view: 'audit',
      message: 'Action denied -- audit chain wrote a tamper-evident entry with the deny reason.',
      action: async () => {
        const a = await fetchAuditIntegrity();
        return { kind: 'audit-recorded', blocks: a?.totalBlocks ?? a?.total, integrity: a?.integrityScore ?? a?.integrity ?? '100%' };
      },
    },
    {
      view: 'steer',
      message: 'Operator triggers fleet halt -- every governed agent stops within milliseconds.',
      action: async () => {
        const h = await postFleetHalt('demo-operator', 'demo: prove halt latency');
        return { kind: 'halt-fleet', halted: h?.halted ?? h?.agentsHalted, auditId: h?.auditId };
      },
    },
    {
      view: 'steer',
      message: 'Resuming the fleet -- governance state restored from postgres + redis.',
      action: async () => {
        const r = await postFleetResume();
        return { kind: 'resume-fleet', resumed: r?.resumed ?? r?.agentsResumed, auditId: r?.auditId };
      },
    },
    {
      view: 'fleet',
      message: 'Demo complete -- engine produced 4 verifiable audit entries you can inspect in the Audit Trail.',
      action: async () => ({ kind: 'demo-complete' }),
    },
  ];

  const start = useCallback((onViewChange: (view: string) => void) => {
    setRunning(true);
    setStep(0);
    setEvidence([]);
    cancelledRef.current = false;
    let current = 0;

    const advance = async () => {
      if (cancelledRef.current || current >= STEPS.length) {
        setRunning(false);
        setStep(0);
        return;
      }
      const s = STEPS[current];
      if (!s) {
        setRunning(false);
        setStep(0);
        return;
      }
      setStep(current);
      onViewChange(s.view);
      try {
        const result = await s.action();
        if (!cancelledRef.current) {
          setEvidence(prev => [...prev, { step: current, message: s.message, ...result }]);
        }
      } catch (err: any) {
        setEvidence(prev => [...prev, { step: current, message: s.message, kind: 'error', error: err?.message || 'unknown' }]);
      }
      current++;
      if (current < STEPS.length && !cancelledRef.current) {
        timerRef.current = setTimeout(advance, 3000);
      } else {
        setRunning(false);
        setStep(0);
      }
    };

    advance();
  }, []);

  const stop = useCallback(() => {
    cancelledRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    setRunning(false);
    setStep(0);
  }, []);

  return { running, step, steps: STEPS, evidence, start, stop };
}

interface DemoStep {
  view: string;
  message: string;
  action: () => Promise<{ kind: string; [key: string]: any }>;
}

interface DemoEvidence {
  step: number;
  message: string;
  kind: string;
  [key: string]: any;
}
